import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Button, Card, Form, Table } from 'react-bootstrap';
import Loading from './components/Loading';
import useAdminData, { responseError } from './useAdminData';
import { adminFetch, AdminAuthError } from './adminAuth';
import { formatDateTime } from './format';
import { savedMessage } from './sitePublish';

export const DOCUMENTS = [
  { id: 'consignment-agreement', label: 'Consignment agreement' },
  { id: 'consignment-tips', label: 'Consignment tips' }
];

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Why a chosen file can't be uploaded, or null */
export function checkFile(file) {
  if (!file) {
    return 'Choose a PDF first.';
  }
  if (file.type !== 'application/pdf') {
    return 'Choose a PDF file.';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return 'The file is over 10 MB.';
  }
  return null;
}

/** Upload URL from the API, the file straight to S3, then the API checks and publishes it */
export async function uploadDocument(docId, file, { fetchImpl = fetch } = {}) {
  const started = await adminFetch(`/admin/site/documents/${docId}/upload-url`, { method: 'POST' });
  if (!started.ok) {
    throw new Error(await responseError(started));
  }
  const { uploadUrl, key } = await started.json();

  const put = await fetchImpl(uploadUrl, { method: 'PUT', headers: { 'Content-Type': 'application/pdf' }, body: file });
  if (!put.ok) {
    throw new Error(`The upload failed (${put.status}). Try again.`);
  }

  const confirmed = await adminFetch(`/admin/site/documents/${docId}`, { method: 'POST', body: { key } });
  if (!confirmed.ok) {
    throw new Error(await responseError(confirmed));
  }
  return confirmed.json();
}

function useSignInAgain() {
  const history = useHistory();
  return err => {
    if (err instanceof AdminAuthError) {
      history.replace('/admin/login');
      return true;
    }
    return false;
  };
}

function DocumentRow({ doc, current, onUploaded }) {
  const signInAgain = useSignInAgain();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  // Bumped after an upload to remount the file input empty; otherwise choosing the same file
  // again wouldn't fire onChange
  const [inputKey, setInputKey] = useState(0);

  const upload = async () => {
    const problem = checkFile(file);
    if (problem) {
      setMessage({ variant: 'danger', text: problem });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const result = await uploadDocument(doc.id, file);
      setMessage(savedMessage(result, `${doc.label} uploaded.`));
      setFile(null);
      setInputKey(key => key + 1);
      onUploaded();
    } catch (err) {
      if (signInAgain(err)) {
        return;
      }
      setMessage({ variant: 'danger', text: err.message });
    }
    setBusy(false);
  };

  return (
    <tr>
      <td>{doc.label}</td>
      <td>
        {current
          ? <>{formatDateTime(current.updatedAt)} <a href={current.url} target="_blank" rel="noopener noreferrer">View</a></>
          : <span className="text-muted">Not uploaded yet</span>}
      </td>
      <td>
        <Form.File
          key={inputKey}
          id={`upload-${doc.id}`}
          label={file ? file.name : 'Choose a PDF'}
          accept="application/pdf"
          custom
          onChange={event => setFile(event.target.files[0] || null)}
        />
        <Button className="mt-2" size="sm" disabled={!file || busy} onClick={upload}>
          {busy ? 'Uploading...' : current ? 'Replace' : 'Upload'}
        </Button>
        {message && <Alert className="mt-2 mb-0" variant={message.variant}>{message.text}</Alert>}
      </td>
    </tr>
  );
}

function RepublishCard({ publishedAt, onPublished }) {
  const signInAgain = useSignInAgain();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const republish = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const response = await adminFetch('/admin/site/publish', { method: 'POST' });
      if (!response.ok) {
        throw new Error(await responseError(response));
      }
      setMessage({ variant: 'success', text: 'The public site is up to date.' });
      onPublished();
    } catch (err) {
      if (signInAgain(err)) {
        return;
      }
      setMessage({ variant: 'danger', text: err.message });
    }
    setBusy(false);
  };

  return (
    <Card>
      <Card.Header>Publishing</Card.Header>
      <Card.Body>
        <p>
          Last published {publishedAt ? formatDateTime(publishedAt) : 'never'}. The site republishes itself when the
          sale status, dates, or documents change. Use Republish if a save said the site didn't update.
        </p>
        {message && <Alert variant={message.variant}>{message.text}</Alert>}
        <Button variant="outline-primary" disabled={busy} onClick={republish}>{busy ? 'Republishing...' : 'Republish'}</Button>
      </Card.Body>
    </Card>
  );
}

export default function SiteContent() {
  const { data, error, loading, waking, reload } = useAdminData('/admin/site');

  if (loading && !data) {
    return <Loading waking={waking} />;
  }
  if (error) {
    return <Alert variant="danger">{error} <Button variant="link" onClick={reload}>Try again</Button></Alert>;
  }

  return (
    <div>
      <h2 className="mb-3">Site content</h2>
      <p>
        Documents for <a href={data.siteUrl} target="_blank" rel="noopener noreferrer">the public site</a> and the
        consignment pages. The sale dates and status are set on the Sale settings page.
      </p>
      <Card className="mb-3">
        <Card.Header>Documents (PDF, up to 10 MB)</Card.Header>
        <Table className="mb-0" responsive>
          <tbody>
            {DOCUMENTS.map(doc => (
              <DocumentRow key={doc.id} doc={doc} current={data.documents[doc.id]} onUploaded={reload} />
            ))}
          </tbody>
        </Table>
      </Card>
      <RepublishCard publishedAt={data.publishedAt} onPublished={reload} />
    </div>
  );
}
